/**
 * BriefIdeaRow
 *
 * A single linked idea inside the brief workspace, shown as a COMPACT row
 * (title + status + a tight Concept->Media->Posts dot rail + click-through to
 * the idea workspace) — the same compact language the command center uses, so
 * a brief's ideas read as a clean list rather than a stack of tall cards.
 *
 * The brief payload carries status + posts but not media counts, so the row
 * fetches collection counts via the same hook/key the idea page uses (shared
 * cache, so navigating in is warm).
 */

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrailCrumb } from "@/core/navigation/trail";
import { useQuery } from "@tanstack/react-query";
import { CheckIcon, ChevronRightIcon, CloseIcon } from "@/components/icons";
import { IdeaStatusBadge } from "@/domains/Ideas/components/IdeaStatusBadge";
import {
  deriveStages,
  summarizePostsProgress,
  MediaCounts,
} from "@/domains/Ideas/ideaStageState";
import { Idea } from "@/domains/Ideas/types";
import { useIdeaCollection } from "@/domains/Ideas/hooks/useIdeaCollection";
import * as collectionsService from "@/services/collectionsService";
import type { MediaTypeCounts } from "@/services/collectionsService";

interface BriefIdeaRowProps {
  idea: Idea;
  clientId: string;
  /** Router options stamping the brief page into the trail, so back returns here. */
  trailNavOptions?: { state: { trail: TrailCrumb[] } };
  /** When set, an Unlink affordance removes the idea from the brief. */
  onRemoveFromBrief?: (idea: Idea) => void;
}

export const BriefIdeaRow: React.FC<BriefIdeaRowProps> = ({
  idea,
  clientId,
  trailNavOptions,
  onRemoveFromBrief,
}) => {
  const navigate = useNavigate();
  const { collectionId } = useIdeaCollection(idea.id, clientId);

  const { data: mediaCounts = {} as MediaTypeCounts } = useQuery({
    queryKey: ["idea-media-type-counts", collectionId],
    queryFn: async (): Promise<MediaTypeCounts> => {
      if (!collectionId || !clientId) return {};
      return collectionsService.getCollectionMediaTypeCounts(clientId, collectionId);
    },
    enabled: Boolean(collectionId && clientId),
  });

  const media: MediaCounts = useMemo(
    () => ({
      photo: mediaCounts.photo ?? 0,
      video: mediaCounts.video ?? 0,
      audio: mediaCounts.audio ?? 0,
      total:
        (mediaCounts.photo ?? 0) +
        (mediaCounts.video ?? 0) +
        (mediaCounts.audio ?? 0),
    }),
    [mediaCounts]
  );

  const stages = useMemo(
    () =>
      deriveStages({
        status: idea.status,
        media,
        posts: summarizePostsProgress(idea.posts),
      }),
    [idea.status, idea.posts, media]
  );

  // Route-relative: BriefDetailView (briefs/:briefId) sits one level under the
  // /app/:clientId base, so the sibling ideas route is one `..` up — matching
  // BriefsView's card→page navigation. (`../../` overshoots to root.)
  const openIdea = () => navigate(`../ideas/${idea.id}`, trailNavOptions);

  return (
    <div className="group flex items-center gap-3 px-1 py-3 hover:bg-gray-50/60 transition-colors">
      <button
        type="button"
        onClick={openIdea}
        className="flex items-center gap-4 min-w-0 flex-1 text-left"
      >
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900 truncate">
            {idea.title?.trim() || "Untitled idea"}
          </span>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <IdeaStatusBadge status={idea.status} />
            {idea.contentType && (
              <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-600">
                {idea.contentType}
              </span>
            )}
          </div>
        </div>

        {/* Compact Concept -> Media -> Posts dot rail. */}
        <div className="hidden sm:flex items-center gap-1.5 shrink-0">
          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              {index > 0 && <span className="w-3 h-px bg-gray-200" aria-hidden />}
              <span
                title={stage.detail ? `${stage.label}: ${stage.detail}` : stage.label}
                className={`flex items-center justify-center w-5 h-5 rounded-full ${stage.dotColor} ${
                  stage.done ? "" : "opacity-70"
                }`}
              >
                {stage.done ? (
                  <CheckIcon className="w-3 h-3 text-white" />
                ) : (
                  <span className="text-[9px] font-bold text-white">
                    {index + 1}
                  </span>
                )}
              </span>
            </React.Fragment>
          ))}
        </div>
      </button>

      {onRemoveFromBrief && (
        <button
          type="button"
          onClick={() => onRemoveFromBrief(idea)}
          title="Remove from brief"
          aria-label="Remove from brief"
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      )}
      <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 shrink-0" />
    </div>
  );
};
